import { useGlobalUI } from '@/components/GlobalUIProvider';

import {
  ColorsV2,
  getToken,
  IconButton,
  Pane,
  useBreakpoint,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import {
  ReactElement,
  useContext,
  useEffect,
  useLayoutEffect,
  ReactNode,
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { ThemeContext, Themes } from '@/components/ThemeProvider';

const SIDEBAR_DARK_MODE_BG = '#212123';
const SIDEBAR_SYNTHWAVE_BG = '#110D1E';

const StyledNav = styled.nav<{
  $sm: boolean;
  $md: boolean;
  $mobileNavWidth?: string;
  $mobilePositionTop?: string;
  $isHoveringBackground?: boolean;
  $isSideNavOpen?: boolean;
  $synthwave?: boolean;
  $noTransition?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: ${props => (props.$md ? '100%' : 'calc(100vh - 6.5rem)')};
  position: ${props => (props.$sm ? 'inherit' : 'absolute')};
  top: ${props => (props.$sm ? 'unset' : props.$mobilePositionTop || '6.5rem')};
  width: ${props =>
    props.$sm
      ? '23.2rem'
      : /* 3.8rem is the calculated value for the default padding and margin applied on the side nav */
        props.$mobileNavWidth || 'calc(100vw - 3.8rem)'};
  min-width: 23.2rem;
  background-color: ${getToken(ColorsV2.GRAY_DARKEST)};
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
  ${({ $isHoveringBackground, $isSideNavOpen }) =>
    $isHoveringBackground &&
    ($isSideNavOpen
      ? css`
          box-shadow:
            0 0 12px rgba(0, 0, 0, 0.3),
            inset -3px 0 0 0 ${getToken(ColorsV2.SECONDARY)};
        `
      : css`
          box-shadow:
            0 0 12px rgba(0, 0, 0, 0.3),
            4px 0 3px -1px ${getToken(ColorsV2.SECONDARY)};
        `)}
  ${({ theme }) =>
    theme.name === Themes.DARK &&
    css`
      background-color: ${SIDEBAR_DARK_MODE_BG};
    `}
  ${({ theme }) =>
    theme.name === Themes.SYNTHWAVE &&
    css`
      background: linear-gradient(
        180deg,
        #110d1e 0%,
        #0c1525 78%,
        #081820 100%
      );
    `}
  overflow-x: hidden;
  transition: ${({ $noTransition }) =>
    $noTransition
      ? 'none'
      : 'width 0.25s ease, min-width 0.25s ease, padding 0.2s ease'};
  border-left-style: solid;
  border-left-width: ${props => (props.$synthwave ? '3px' : '6px')};
  border-image-slice: 1;
  border-image-source: ${getToken(ColorsV2.GRADIENT_NAV)};
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  padding: 0 1.6rem;
  /* z-index puts this above the top nav */
  z-index: 3;

  /* Positioning context and color for top-level nav items */
  & > a.nav-item,
  & > div.collapsible-subnav {
    position: relative;
    color: ${getToken(ColorsV2.WHITE_STATIC)};
    border-radius: 8px;
    transition: background-color 0.25s ease;
  }

  /* Sub-nav items fill past the items container's left padding — revert this block to undo */
  & > div.collapsible-subnav .collapsible-subnav__items a.nav-item {
    border-radius: 8px;
    transition: background-color 0.25s ease;
  }

  /* Left border track for sub-nav group */
  & > div.collapsible-subnav .collapsible-subnav__items {
    padding-left: 1.6rem;

    & > div {
      width: 100%;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: -0.8rem;
        height: 90%;
        width: 2px;
        background: ${getToken(ColorsV2.PRIMARY_LIGHT)};
      }
    }
  }

  /* Electric Logo for desktop/tablet, first nav item for mobile */
  & > :first-child {
    margin: ${props => (props.$md ? '2rem 0 2rem 0.4rem' : '3rem 0 2rem')};
  }

  /* First nav item for desktop/tablet gets more top margin */
  & > :nth-child(2) {
    margin-top: ${props => (props.$md ? '2rem' : undefined)};
  }

  /* Collapsed icon label — hidden by default, shown only when nav is collapsed */
  [data-nav-label] {
    display: none;
  }

  /* Section headings animate out when nav collapses */
  & > .section-heading {
    overflow: hidden;
    min-height: fit-content;
    max-height: 5rem;
    opacity: 1;
    transition: ${({ $noTransition }) =>
      $noTransition
        ? 'none'
        : 'max-height 0.25s ease, opacity 0.15s ease, margin-top 0.25s ease, margin-bottom 0.25s ease'};
  }

  /* If the side nav has the collapsed class, apply these styles */
  ${({ className }) =>
    className &&
    className.includes('sidenav-turbine--collapsed') &&
    `
      width: 4.5rem;
      min-width: 4.5rem;
      padding: 0 1.2rem;

      /* All children nav item divs */
      & > a, div.collapsible-subnav button {
        padding: 0;
      }

      & > .section-heading {
        min-height: 0;
        max-height: 0;
        opacity: 0;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }

      a.nav-item,
      div.collapsible-subnav > .collapsible-subnav__section {
        height: auto;
        min-height: 4.5rem;
        line-height: normal;

        div[data-arc='icon'] {
          margin-right: 0;
        }

        div[data-testid='arc-icon'] {
          margin-left: 0;
        }

        /* Show the collapsed icon label */
        [data-nav-label] {
          display: block;
        }

        /* Center label icon & text */
        [data-nav-icon-label-container] {
          width: 100%;
        }

        /* Hides the text, chip, and chevron when nav is collapsed */
        [data-arc="text"], [data-arc="chip"], span.collapsible-subnav__text,
        div[data-testid='nav-collapsible-icon-arrow'] {
          display: none;
        }
      }

      div.collapsible-subnav {
        &__section {
          padding-top: 0.8rem;
        }

        &__items {
          display: none;
          transition: none;
        }
      }

      /* Electric logo clipping */
      .nav-logo {
        clip-path: inset(0 75px 0 0);
        margin-left: 0.8rem;
      }
  `}
`;
const NavBackgroundClickArea = styled.div`
  flex: 1;
  width: calc(100% + 3.2rem);
  margin: 0 -1.6rem;
  cursor: pointer;
`;

const NavToggleIconButton = styled(IconButton)<{ $synthwave?: boolean }>`
  /* Suppress Arc's built-in hover background so only the container animates */
  &:hover {
    background-color: transparent !important;
  }

  div[data-arc='icon'] svg {
    color: ${getToken(ColorsV2.WHITE_STATIC)} !important;
    transition: color 0.15s ease;
  }

  ${({ $synthwave }) =>
    $synthwave &&
    css`
      &:hover div[data-arc='icon'] svg {
        color: ${getToken(ColorsV2.PRIMARY)} !important;
      }
    `}
`;

const ToggleButtonPane = styled(Pane)`
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: ${getToken(ColorsV2.PRIMARY_DARK)} !important;
    border-color: ${getToken(ColorsV2.PRIMARY_DARK)} !important;
  }
`;

const ToggleCollapseElement = ({
  onManualToggle,
  isSideNavOpen,
}: {
  onManualToggle: () => void;
  isSideNavOpen: boolean;
}) => {
  const { t } = useTranslation('common');
  const { theme } = useContext(ThemeContext);
  const isSynthwave = theme.name === Themes.SYNTHWAVE;
  const isDark = theme.name === Themes.DARK;
  const bgColor = isSynthwave
    ? SIDEBAR_SYNTHWAVE_BG
    : isDark
      ? SIDEBAR_DARK_MODE_BG
      : ColorsV2.GRAY_DARKEST;
  const arrowIcon = !isSideNavOpen ? 'chevron-right' : 'chevron-left';
  return (
    <ToggleButtonPane
      borderRadius="50%"
      width="fit-content"
      height="fit-content"
      position="absolute"
      bottom="50px"
      left={isSideNavOpen ? '250px' : '55px'}
      backgroundColor={bgColor}
      border="1px solid rgba(255, 255, 255, 0.2)">
      <NavToggleIconButton
        $synthwave={isSynthwave}
        id="side-nav-arrow-button"
        data-testid="side-nav-arrow-button"
        icon={arrowIcon}
        intent="neutral"
        iconSize="xsmall"
        onClick={onManualToggle}
        aria-label={t('sideNav.toggleCollapse')}
      />
    </ToggleButtonPane>
  );
};

// Define a common props interface that your nav children should implement
export interface NavChildProps {
  $toggleSideNav?: () => void;
  $isSideNavCollapsed?: boolean;
}

type SideNavComponentProps = {
  children: ReactNode;
  mobileNavWidth?: string;
  mobilePositionTop?: string;
};

const SideNavComponent = ({
  children,
  mobileNavWidth,
  mobilePositionTop,
}: SideNavComponentProps): ReactElement => {
  const { sm, md, lg } = useBreakpoint();
  const {
    closeSideNav,
    manualToggleSideNav,
    restoreSavedNavPreference,
    isSideNavOpen,
  } = useGlobalUI();
  const { theme } = useContext(ThemeContext);
  const isSynthwave = theme?.name === Themes.SYNTHWAVE;

  const [isHoveringBackground, setIsHoveringBackground] = useState(false);
  // Suppresses the width transition on initial mount so the breakpoint
  // correction (useLayoutEffect below) doesn't play as a visible animation.
  const [isReady, setIsReady] = useState(false);

  // Collapse the side nav on render if the screen is medium and below.
  // On expand, restore the user's saved preference rather than forcing open.
  const isCollapsedOnRender = Boolean(md) && !lg;
  useLayoutEffect(() => {
    isCollapsedOnRender ? closeSideNav() : restoreSavedNavPreference();
  }, [isCollapsedOnRender, closeSideNav, restoreSavedNavPreference]);

  // Enable transitions after the first paint so the initial state correction
  // above never shows as an animation.
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Map through children and clone each with additional props
  const childrenWithProps = Children.map(children, child => {
    // Check if the child is a valid React element
    if (isValidElement<PropsWithChildren<NavChildProps>>(child)) {
      return cloneElement(child, {
        $toggleSideNav: manualToggleSideNav,
        $isSideNavCollapsed: !isSideNavOpen,
      });
    }
    return child;
  });

  return (
    <StyledNav
      data-testid="sidenav"
      aria-label="Main"
      $sm={Boolean(sm)}
      $mobileNavWidth={mobileNavWidth}
      $mobilePositionTop={mobilePositionTop}
      $md={Boolean(md)}
      $isHoveringBackground={isHoveringBackground}
      $isSideNavOpen={!!isSideNavOpen}
      $synthwave={isSynthwave}
      $noTransition={!isReady}
      className={`sidenav-turbine${
        !isSideNavOpen ? ' sidenav-turbine--collapsed' : ''
      }`}>
      {childrenWithProps}
      {md && (
        <NavBackgroundClickArea
          aria-hidden="true"
          onClick={manualToggleSideNav}
          onMouseEnter={() => setIsHoveringBackground(true)}
          onMouseLeave={() => setIsHoveringBackground(false)}
        />
      )}
      {md && (
        <ToggleCollapseElement
          onManualToggle={manualToggleSideNav}
          isSideNavOpen={!!isSideNavOpen}
        />
      )}
    </StyledNav>
  );
};

export default SideNavComponent;
